// Bottom input dock (TASK-499, ports SixEase shell/InputDock). The single input on the
// canvas: shortcuts, quick-commands, slash-commands and natural language. There is no
// separate chat panel — a turn is a ChatMessage spec pushed into the same canvasStore
// sink, so dialogue and summoned widgets interleave in the canvas (see Canvas BARE set).
//
// TASK-712: NL speaks /ws/chat via the shared useAgenticChat hook. Server-side, the
// classify-once dispatcher (TASK-706's fast pass) answers view/clarify immediately and
// streams the agent loop for work turns — tokens, tool progress, hard-gate — on ONE
// conversation_id. REST /api/orchestrate-ui remains only as the offline fallback.
import { useEffect, useMemo, useRef, useState } from 'react';
import { notifyToolFinished } from '../../stores/widgetRefreshStore';
import CreateArtifactDialog from '../documents/CreateArtifactDialog';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Activity, ChevronDown, ChevronUp, GitCompareArrows, Loader2, Mic, Paperclip, PieChart, Plus, Send, ShieldCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { usePersistentToggle } from '../../hooks/usePersistentToggle';
import {
  useCanvasStore,
  openDocumentFromWidget,
  selectLastClientId,
  selectLastSessionId,
  previewActions,
} from '../../stores/canvasStore';
import { orchestrateApiService, type ChatTurn } from '../../services/api/orchestrateApiService';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { transcribeVoiceNote } from '../../services/voiceNoteService';
import { createWorkbenchDocument, type WorkbenchFormat } from '../../services/api/documentApi';
import type { WidgetSpec } from '../../registry/types';
import {
  useAgenticChat,
  type AgenticChunk,
  type HardGateConfirm,
} from '../../hooks/useAgenticChat';
import { canvasScope, threadKeyForScope } from './canvasThread';
import { useFocusStore } from '../../stores/focusStore';
import { useDockStore } from '../../stores/dockStore';
import { getDockChatStore } from '../../stores/dockChatStores';
import { useAccount } from '../../contexts/AccountContext';
import {
  generateChatMessageId,
  resolvePrompt,
  promptMessageContent,
  type RequestedPrompt,
} from '../../stores/createChatStore';
import { SCOPE_CHUNK_CONTRACTS } from '../../types/chunkContracts';
import { ChatBubble } from '../dock/ChatBubble';
import { ToolProgress, type ToolProgressEvent } from '../dock/ToolProgress';
import { ConnectionPill } from '../dock/ConnectionPill';
import { ActionProposalCard } from '../meeting/preparation/ActionProposalCard';
import type { ProposalActionItem } from '../../types/prepChat.types';
import { ConfirmDialog } from '../ui/confirm-dialog';

// One-click summons (SixEase parity). Book-level chips always work; client chips target
// the canvas-focus client. `pick` is the widget a client-scoped chip opens per client —
// with none in focus, the chip summons ClientBook as a PICKER into that view (openComponent),
// so choosing a row lands there. runShortcut() applies this.
const SHORTCUTS: { key: string; label: string; Icon: typeof Activity; build: (clientId: string) => WidgetSpec[]; pick?: string }[] = [
  // TASK-575: Radar chip removed — ChangeRadar is now the always-present NOTICE column.
  { key: 'clients', label: 'Clients', Icon: Users, build: () => [{ component: 'ClientBook', props: {} }] },
  // TASK-666 — book-level: no `pick`, so it works with no client in focus.
  { key: 'meetings', label: 'Meetings', Icon: CalendarDays, build: () => [{ component: 'MeetingsList', props: {} }] },
  {
    key: 'portfolio', label: 'Portfolio', Icon: PieChart,
    build: (cid) => [{ component: 'CockpitPortfolioView', props: { clientId: cid } }], pick: 'CockpitPortfolioView',
  },
  {
    key: 'values', label: 'Values', Icon: ShieldCheck,
    build: (cid) => [{ component: 'DnaCard', props: { clientId: cid } }, { component: 'ConflictsList', props: { clientId: cid } }], pick: 'DnaCard',
  },
  {
    key: 'rebalance', label: 'Rebalance', Icon: GitCompareArrows,
    build: (cid) => [{ component: 'BeforeAfter', props: { clientId: cid } }], pick: 'BeforeAfter',
  },
];

// TASK-670: was "…with Huber" — the one demo client with NO meeting session, so the
// most-clicked path into MeetingPrep was guaranteed to show the degraded widget. Schneider has
// meeting_hubertus01 + a seeded brief, so the starter shows the full view.
// Deep review #5: exported because `Canvas.STARTERS` offers the same prompt on the empty canvas
// and was still sending RMs to Huber — one string, one place to retarget it.
export const PREP_STARTER = 'Prep me for my meeting with Schneider';

// Daily-triage NL starters, retargeted to the seeded hero persona (Schneider).
const QUICK_COMMANDS = [
  "Which clients need attention?",
  "Show Schneider's portfolio",
  PREP_STARTER,
  'Draft an email to Schneider',
] as const;

// Local slash-commands that need no network. Named-client slashes (/client, /portfolio)
// fall through to the brain, which is the client-resolution authority anyway.
// ponytail: local client-list resolution + client autocomplete deferred — the brain
// resolves names, so a client roster in the dock would just duplicate that.
function resolveSlashCommand(text: string): WidgetSpec[] | null {
  const [cmd, ...rest] = text.trim().split(/\s+/);
  const arg = rest.join(' ').trim();
  switch (cmd.toLowerCase()) {
    case '/book':
    case '/clients':
      return [{ component: 'ClientBook', props: {} }];
    case '/radar':
      return [{ component: 'ChangeRadar', props: {} }];
    case '/client':
      return arg ? null : [{ component: 'ClientBook', props: {} }]; // no name → book (rows open default view); name → NL
    case '/portfolio':
      // no name → book as a portfolio picker (openComponent); name → NL (brain resolves).
      return arg
        ? null
        : [{ component: 'ClientBook', props: { title: 'Select a client — Portfolio', openComponent: 'CockpitPortfolioView' } }];
    default:
      return null; // unknown slash → treat as NL
  }
}

// "CockpitPortfolioView" → "cockpit portfolio" for turn declarations.
//
// TASK-871: the trailing "View"/"Tab"/"Widget" is dropped, because the sentence that consumes
// this appends its own noun — "…refreshed the existing cockpit portfolio view view rather than
// adding a second copy", observed live. The component name carries the word; the sentence
// carries the word; only one of them should.
export const humanizeComponent = (component: string) =>
  component
    .replace(/(?<=[a-z0-9])(?=[A-Z])/g, ' ')
    // An acronym boundary: "KYCTab" has no lowercase before the T, so the rule above never
    // split it and the sentence read "the existing kyctab view".
    .replace(/(?<=[A-Z])(?=[A-Z][a-z])/g, ' ')
    .replace(/\s+(View|Tab|Widget|Card|List)$/i, '')
    .toLowerCase();

export function InputDock() {
  const { i18n } = useTranslation();
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  // TASK-746: the one create dialog, mounted on the dashboard dock.
  const [createOpen, setCreateOpen] = useState(false);
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  const setPendingStatus = useCanvasStore((s) => s.setPendingStatus);
  const lastClientId = useCanvasStore(selectLastClientId);
  // TASK-707: the meeting on the canvas, so "review this meeting" resolves to the one the
  // RM is looking at. A hint — the server checks it against the focused client's meetings.
  const lastSessionId = useCanvasStore(selectLastSessionId);
  // TASK-714/715: the dock scope tracks the topmost focus popup — a meeting popup
  // in front means the turn belongs to THAT meeting: its conversation thread, its
  // context envelope (via the popup's registered contextProvider), and its side
  // channels (brief updates / proposals → the meeting-scope store the popup reads).
  const focusTop = useFocusStore((s) => (s.stack.length > 0 ? s.stack[s.stack.length - 1] : null));
  const effectiveSessionId = focusTop?.workspace === 'meeting' ? focusTop.entityId : lastSessionId;
  const { account } = useAccount();
  const meetingStore =
    focusTop?.workspace === 'meeting'
      ? getDockChatStore(account?.id ?? 'unknown', {
          scopeType: 'meeting',
          entityId: focusTop.entityId,
        })
      : null;
  const meetingStoreRef = useRef(meetingStore);
  meetingStoreRef.current = meetingStore;
  const pendingPrompt = useCanvasStore((s) => s.pendingPrompt);
  const clearPendingPrompt = useCanvasStore((s) => s.clearPendingPrompt);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recorder = useAudioRecorder();
  const navigate = useNavigate();

  // TASK-712: history + conversation id live in the dock store (canvas
  // clear()/undo() resets them with the bubbles — see Canvas.tsx).
  //
  // TASK-795: that store is SCOPED now. It was one unscoped singleton, which is
  // why a task delegated from /dashboard could not keep its conversation and
  // TASK-788 had to suppress the thread here. Same placement+thread composition
  // as Dock.tsx; the meeting-popup store above is a separate, unchanged axis.
  const activeTaskThread = useDockStore((s) => s.activeTaskThread);
  const scope = useMemo(
    // A meeting popup in front is a placement that names an entity — see
    // `canvasScope`. Passing it is what keeps client B's meeting turn out of
    // client A's task thread.
    () => canvasScope(activeTaskThread, focusTop?.workspace === 'meeting' ? focusTop.entityId : null),
    [activeTaskThread, focusTop?.workspace, focusTop?.entityId]
  );
  const threadKey = threadKeyForScope(scope);
  const useCanvasChatStore = useMemo(
    () => getDockChatStore(account?.id ?? 'unknown', scope),
    [account?.id, scope.scopeType, scope.entityId] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const conversationId = useCanvasChatStore((s) => s.conversationId);

  /**
   * The store and thread key the CURRENT turn started in.
   *
   * `useAgenticChat` keeps `onMessageComplete`/`onError` in refs it refreshes
   * every render, precisely so they never go stale — which means a thread
   * opened mid-turn (delegating from a task card while an answer streams) would
   * otherwise land that answer, and its server `conversationId`, in the NEW
   * store. The wrong-conversation-id half is durable: every later turn in the
   * task thread would continue the dashboard's conversation. The meeting axis
   * already pins itself this way (`meetingStoreRef`); the primary store needs
   * the same, so a turn completes where it began.
   */
  const turnRef = useRef<{ store: typeof useCanvasChatStore; threadKey: string } | null>(null);
  const liveTurn = () => turnRef.current ?? { store: useCanvasChatStore, threadKey };
  const dockAddMessage: ReturnType<typeof useCanvasChatStore.getState>['addMessage'] = (msg) =>
    liveTurn().store.getState().addMessage(msg);

  // Widget chunks buffer here until `done`, so the assistant bubble and its
  // widgets land on the canvas together (and the refreshed-note stays correct).
  const pendingWidgetsRef = useRef<WidgetSpec[]>([]);
  const inFlightQueryRef = useRef<string | null>(null);
  const [toolEvents, setToolEvents] = useState<ToolProgressEvent[]>([]);
  // TASK-869: task proposals raised from the canvas. `propose_action` is granted to the
  // dashboard/portfolio specialists, and its Apply card had nowhere to render here — the
  // chunk was routed to the meeting store, which is null unless a meeting popup is
  // focused, so the advisor was told a task was put up and saw nothing. Held locally
  // rather than in the canvas spec store because the card is a turn artefact, not a
  // canvas widget: it disappears when the next question is asked.
  const [actionProposals, setActionProposals] = useState<ProposalActionItem[]>([]);

  // TASK-746: "Generate from description" routes through the agent — the
  // dialog prefills this input; the user reviews and sends.
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const detail = (e as CustomEvent<{ text?: string }>).detail;
      if (detail?.text) setValue(detail.text);
    };
    window.addEventListener('kielis:dock-prefill', onPrefill);
    return () => window.removeEventListener('kielis:dock-prefill', onPrefill);
  }, []);
  const [pendingConfirm, setPendingConfirm] = useState<HardGateConfirm | null>(null);

  // Shared assembly for WS and REST turns: refreshed-note + bubble + widgets.
  function completeTurn(reply: string, specs: WidgetSpec[]) {
    const outcomes = previewActions(useCanvasStore.getState().specs, specs);
    const updated = outcomes.filter((o) => o.action === 'updated');
    const finalReply =
      updated.length > 0
        ? `${reply}\n\n↻ This matched a view already on your canvas, so I refreshed the existing ${updated
            .map((u) => humanizeComponent(u.component))
            .join(', ')} view${updated.length === 1 ? '' : 's'} rather than adding a second copy.`
        : reply;
    addSpecs([{ component: 'ChatMessage', props: { role: 'assistant', text: finalReply }, threadKey: liveTurn().threadKey }, ...specs]);
    dockAddMessage({
      id: generateChatMessageId('dock'),
      role: 'assistant',
      content: reply,
      timestamp: new Date().toISOString(),
    });
  }

  const {
    isConnected,
    isReconnecting,
    isStreaming: wsStreaming,
    streamingContent,
    sendMessage: wsSendMessage,
    sendConfirm: wsSendConfirm,
    connect,
  } = useAgenticChat({
    logLabel: 'dashboard-dock',
    onMessageComplete: (content, convId) => {
      const specs = pendingWidgetsRef.current;
      pendingWidgetsRef.current = [];
      inFlightQueryRef.current = null;
      completeTurn(content, specs);
      // TASK-715: with a meeting popup open the turn ALSO belongs to that
      // meeting's thread — its side channels commit there for the popup to read.
      const ms = meetingStoreRef.current;
      if (ms) {
        ms.getState().addMessage({
          id: generateChatMessageId('dock'),
          role: 'assistant',
          content,
          timestamp: new Date().toISOString(),
        });
        ms.getState().commitPending();
        if (convId) ms.getState().setConversationId(convId);
      } else if (convId) {
        liveTurn().store.getState().setConversationId(convId);
      }
      turnRef.current = null;
      setToolEvents([]);
      setLoading(false);
      setPendingStatus(null);
    },
    onError: (err) => {
      pendingWidgetsRef.current = [];
      const query = inFlightQueryRef.current;
      inFlightQueryRef.current = null;
      setToolEvents([]);
      setLoading(false);
      setPendingStatus(null);
      addSpecs([{ component: 'ChatMessage', props: { role: 'assistant', text: "Sorry — I couldn't reach the workbench just now." }, threadKey: liveTurn().threadKey }]);
      turnRef.current = null;
      console.warn('[dashboard-dock] turn failed:', err);
      if (query) {
        toast.error('Request failed', { action: { label: 'Retry', onClick: () => submit(query, { isRetry: true }) } });
      }
    },
    handlers: {
      widget: (chunk: AgenticChunk) => {
        const widget = chunk.widget;
        if (!widget?.component) return;
        // OpenDocument is a navigation handoff, not a tile (TASK-640) — route it
        // to the pending-open slot OpenDocumentNavigator consumes. Since TASK-742
        // only an explicit open_document emits it (creation emits ArtifactCard,
        // which buffers below like any tile); TASK-717 makes Open location-aware
        // (workbench popup, blocked on TASK-738).
        if (widget.component === 'OpenDocument') {
          openDocumentFromWidget(widget.props);
          return;
        }
        pendingWidgetsRef.current.push({ component: widget.component, props: widget.props ?? {} });
      },
      status: (chunk: AgenticChunk) => {
        if (chunk.status) setPendingStatus(chunk.status);
      },
      tool_start: (chunk: AgenticChunk) => {
        const tool = (chunk.tool_name as string) || 'tool';
        setPendingStatus(null);
        setToolEvents((prev) => [
          ...prev,
          { id: `${tool}-${prev.length}`, label: `Running ${tool}…`, status: 'running', startedAt: Date.now() },
        ]);
      },
      tool_end: (chunk: AgenticChunk) => {
        const failed = chunk.status === 'failed';
        // A finished tool may have changed what a mounted widget is showing; the widget
        // declares which tools matter to it (see `useRefreshTick`).
        if (!failed && chunk.tool_name) notifyToolFinished(chunk.tool_name as string);
        setToolEvents((prev) => {
          const idx = prev.findIndex((e) => e.status === 'running');
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], status: failed ? 'failed' : 'completed', endedAt: Date.now() };
          return next;
        });
      },
      hard_gate_confirm: (chunk: AgenticChunk) => {
        // TASK-797: deliberately does NOT `setTurn('blocked')`, unlike the dock
        // and the rail. This surface gates its input on the local `loading` flag
        // rather than on `isTurnActive`, so it never joined TASK-792's state
        // machine and never had that ticket's dead-end. Adding the call here
        // would import the bug into the one placement that does not have it.
        // Bringing the canvas into the machine is TASK-795's surface, not this.
        if (chunk.confirm) setPendingConfirm(chunk.confirm);
      },
      // TASK-715: meeting-popup side channels → the meeting-scope store.
      brief_update: (chunk: AgenticChunk) => {
        if (chunk.brief_update)
          meetingStoreRef.current?.getState().addPending('briefUpdates', chunk.brief_update as never);
      },
      proposal_action: (chunk: AgenticChunk) => {
        if (chunk.proposal_action)
          meetingStoreRef.current?.getState().addPending('proposalActions', chunk.proposal_action as never);
      },
      action_proposal: (chunk: AgenticChunk) => {
        if (!chunk.action_proposal) return;
        // With a meeting popup focused the dock scope-switches to it (TASK-715) and the
        // meeting surface renders the card. Otherwise it belongs to this turn, here.
        if (meetingStoreRef.current) {
          meetingStoreRef.current.getState().addPending('actionProposals', chunk.action_proposal as never);
          return;
        }
        setActionProposals((prev) => [...prev, chunk.action_proposal as ProposalActionItem]);
      },
    },
    ignored: SCOPE_CHUNK_CONTRACTS.dashboard.ignored,
    assistantNoun: 'assistant',
  });

  // Connect once on mount; the hook cleans up on unmount.
  useEffect(() => {
    connect();
  }, [connect]);

  // TASK-650: chip rows collapse after first use (less dock height over the
  // canvas) and stay recoverable via the toolbar chevron. Persisted preference.
  const [chipsVisible, toggleChips, setChipsVisible] = usePersistentToggle('kielis-dock-chips', true);

  // Upload or create a workbench document from the canvas, then land in the
  // editor at /workbench/:docId (same flow as the Documents-page Files tab).
  async function uploadDocument(file: File) {
    setDocBusy(true);
    try {
      const res = await createWorkbenchDocument({ file });
      toast.success(`Uploaded ${file.name}`);
      navigate(`/workbench/${res.document_id}`);
    } catch (err) {
      toast.error(`Upload failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setDocBusy(false);
    }
  }

  async function createBlankDocument(kind: string) {
    setDocBusy(true);
    try {
      const format: WorkbenchFormat =
        kind === 'sheet' || kind === 'xlsx' ? 'xlsx'
        : kind === 'deck' || kind === 'pptx' ? 'pptx'
        : 'docx';
      const titles: Record<WorkbenchFormat, string> = {
        docx: 'Untitled document',
        xlsx: 'Untitled sheet',
        pptx: 'Untitled deck',
        pdf: 'Untitled PDF', // unreachable: PDFs are uploaded/imported, never created blank
      };
      const res = await createWorkbenchDocument({ format, title: titles[format] });
      navigate(`/workbench/${res.document_id}`);
    } catch (err) {
      toast.error(`Couldn't create document: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setDocBusy(false);
    }
  }

  // SixEase-faithful (ports InputDock runShortcut): a client-scoped chip with no client
  // in focus summons ClientBook as a picker into that chip's view; otherwise it builds
  // the view for the focused client.
  function runShortcut(s: (typeof SHORTCUTS)[number]) {
    setChipsVisible(false); // TASK-650: chips collapse after use
    if (s.pick && !lastClientId) {
      addSpecs([{ component: 'ClientBook', props: { title: `Select a client — ${s.label}`, openComponent: s.pick } }]);
      return;
    }
    addSpecs(s.build(lastClientId ?? ''));
  }

  async function submit(prompt: RequestedPrompt = value, opts?: { isRetry?: boolean }) {
    // TASK-767: `query` is what goes on the wire (routing depends on its exact
    // shape); `shown` is what the advisor reads in the bubble. They are the same
    // string for anything typed here — they differ only for a handed-over
    // delegation, which carries task/client ids and `work_task`.
    const { display, payload } = resolvePrompt(prompt);
    const query = payload.trim();
    const shown = display.trim() || query;
    if (!query || loading) return;
    // TASK-795: pin the turn to the store it STARTS in. Everything after this —
    // the assistant bubble, the message, the conversation id, a retry — belongs
    // to this thread even if the advisor opens another one while it is in flight.
    turnRef.current = { store: useCanvasChatStore, threadKey };

    // Slash-command fast path (local, no network) for book/radar.
    if (query.startsWith('/')) {
      // /new [doc|sheet|deck] → blank workbench document, opens the editor.
      const [cmd, arg] = query.split(/\s+/);
      if (cmd.toLowerCase() === '/new') {
        setValue('');
        void createBlankDocument((arg ?? 'doc').toLowerCase());
        return;
      }
      const specs = resolveSlashCommand(query);
      setValue('');
      if (specs) {
        addSpecs(specs);
        return;
      }
      // /client <name> or /portfolio <name> → fall through to the brain as NL.
    } else {
      setValue('');
    }

    // User bubble renders immediately — but a retry already has one on the canvas.
    if (!opts?.isRetry) {
      addSpecs([{ component: 'ChatMessage', props: { role: 'user', text: shown }, threadKey }]);
    }
    dockAddMessage({
      id: generateChatMessageId('dock'),
      role: 'user',
      // `content` stays the payload: it is this store that feeds the offline
      // REST history, and the model must see what it was actually sent.
      ...promptMessageContent({ display: shown, payload: query }),
      timestamp: new Date().toISOString(),
    });
    setLoading(true);
    setActionProposals([]);
    // TASK-601 staged loading: skeleton tile with a status line while the LLM
    // round-trip is in flight; flips just before the widgets mount (their own
    // loaders take over the data phase).
    setPendingStatus('choosing view…');

    // TASK-712: WS first — the dispatcher classifies once server-side and either
    // answers immediately (view/clarify) or streams the agent loop (work).
    pendingWidgetsRef.current = [];
    inFlightQueryRef.current = query;

    // TASK-715: with a meeting popup open, the popup's registered contextProvider
    // supplies the meeting envelope (route /meeting/:id/prepare → meeting
    // specialist, live brief/portfolio/proposals) and the turn rides the
    // MEETING thread's conversation id.
    const provided = meetingStore ? useDockStore.getState().contextProvider?.() : null;
    const sendConversationId = meetingStore
      ? meetingStore.getState().conversationId
      : conversationId;
    if (meetingStore) {
      meetingStore.getState().addMessage({
        id: generateChatMessageId('dock'),
        role: 'user',
        ...promptMessageContent({ display: shown, payload: query }),
        timestamp: new Date().toISOString(),
      });
    }
    const sent = wsSendMessage(
      query,
      sendConversationId,
      provided
        ? {
            current_route: provided.current_route ?? window.location.pathname,
            current_entity: provided.entity,
            page_data: { ...(provided.page_data ?? {}), language: i18n.language },
          }
        : {
            current_route: window.location.pathname,
            page_data: {
              surface: 'dock',
              client_id: lastClientId ?? null,
              session_id: effectiveSessionId ?? null,
              // TASK-651: generated replies follow the active UI language.
              language: i18n.language,
            },
          }
    );
    if (sent) return; // chunks drive the rest (handlers above)

    // Offline fallback: the REST fast path (no streaming, view/clarify + inline
    // escalation — pre-712 behavior). History from the dock store.
    inFlightQueryRef.current = null;
    try {
      // Prior turns only — the current query was just pushed and resolve_query
      // appends it itself.
      const history: ChatTurn[] = liveTurn()
        .store.getState()
        .messages.slice(0, -1)
        .slice(-8)
        .map((m) => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content }));
      const res = await orchestrateApiService.resolve({
        query,
        client_id: lastClientId ?? null,
        session_id: effectiveSessionId ?? null,
        route: window.location.pathname,
        history,
        language: i18n.language,
      });
      setPendingStatus('loading data…');
      // TASK-651/707: the refreshed-note logic lives in completeTurn.
      completeTurn(res.reply, res.specs);
    } catch {
      addSpecs([{ component: 'ChatMessage', props: { role: 'assistant', text: "Sorry — I couldn't reach the workbench just now." }, threadKey }]);
      toast.error('Request failed', { action: { label: 'Retry', onClick: () => submit(query, { isRetry: true }) } });
    } finally {
      setLoading(false);
      setPendingStatus(null);
    }
  }

  async function toggleMic() {
    if (recorder.state === 'recording') {
      setTranscribing(true);
      try {
        const blob = await recorder.stop();
        if (blob.size < 1200) return; // too short to be speech
        const { transcript } = await transcribeVoiceNote(blob);
        if (transcript) setValue((v) => (v ? `${v} ${transcript}` : transcript));
      } catch {
        toast.error('Could not transcribe audio');
      } finally {
        setTranscribing(false);
      }
    } else {
      const ok = await recorder.start();
      if (!ok) toast.error('Microphone unavailable');
    }
  }

  const recording = recorder.state === 'recording';

  // TASK-768: announce that requestPrompt has somewhere to land. Surfaces that
  // offer a prompt (the Canvas empty-state starters) hide theirs when this dock
  // is not mounted, rather than rendering a control that cannot fire.
  useEffect(() => useCanvasStore.getState().registerPromptConsumer(), []);

  // TASK-650: empty-canvas starters submit through this dock (one pipeline).
  //
  // TASK-795: held, not dropped, while a turn is in flight. `submit` bails on
  // `loading`, so clearing first meant a delegation arriving mid-turn vanished —
  // and since delegation now also opens a task thread, that left a named
  // `task:<id>` thread which never received a message: the TASK-788 defect, from
  // a new direction. The effect re-runs when `loading` clears, so the prompt is
  // consumed only once it can actually be sent.
  useEffect(() => {
    if (pendingPrompt && !loading) {
      clearPendingPrompt();
      void submit(pendingPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit is recreated per render; pendingPrompt/loading are the triggers
  }, [pendingPrompt, loading]);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-border bg-background/95 backdrop-blur px-4 py-2.5">
      <div className="mx-auto max-w-5xl">
        {/* TASK-712: live turn preview — streamed tokens + tool progress for work
            turns. The finished reply commits to the canvas as a bubble on done. */}
        {(toolEvents.length > 0 || (wsStreaming && streamingContent)) && (
          <div className="mb-2 space-y-2" data-testid="dock-live-turn">
            {toolEvents.length > 0 && <ToolProgress events={toolEvents} />}
            {wsStreaming && streamingContent && (
              <ChatBubble role="assistant" content={streamingContent} streaming />
            )}
          </div>
        )}
        {/* TASK-869: the Apply/Skip card for a task the assistant put up. Apply POSTs
            /api/tasks; nothing is written until the advisor clicks it. */}
        {actionProposals.length > 0 && (
          <div className="mb-2 space-y-2" data-testid="canvas-action-proposals">
            {actionProposals.map((p, i) => (
              <ActionProposalCard key={`canvas-ap-${i}`} proposal={p} />
            ))}
          </div>
        )}
        {(!isConnected || isReconnecting) && (
          <div className="mb-1.5 flex justify-end">
            <ConnectionPill
              isConnected={isConnected}
              isReconnecting={isReconnecting}
              onReconnect={connect}
            />
          </div>
        )}
        {/* TASK-457/712: hard-gated action needs an explicit confirm. */}
        {pendingConfirm && (
          <ConfirmDialog
            open
            onOpenChange={(open) => {
              // Closing without confirming is a cancel — the hard gate never
              // runs on silence.
              if (!open) {
                wsSendConfirm(pendingConfirm.action_id, 'cancel', conversationId);
                setPendingConfirm(null);
              }
            }}
            title={pendingConfirm.summary || `Confirm ${pendingConfirm.tool_name}`}
            description={pendingConfirm.risk || 'This action needs your explicit confirmation.'}
            onConfirm={() => {
              wsSendConfirm(pendingConfirm.action_id, 'confirm', conversationId);
              setPendingConfirm(null);
            }}
          />
        )}
        {chipsVisible && (
          <>
            {/* Shortcut chips */}
            <div className="mb-2 flex flex-wrap gap-1.5">
              {SHORTCUTS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => runShortcut(s)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <s.Icon className="h-3.5 w-3.5" />
                  {s.label}
                </button>
              ))}
            </div>

            {/* Quick-command chips */}
            <div className="mb-2 flex flex-wrap gap-1.5">
              {QUICK_COMMANDS.map((cmd) => (
                <button
                  key={cmd}
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setChipsVisible(false); // TASK-650: chips collapse after use
                    void submit(cmd);
                  }}
                  className="rounded-full bg-accent px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                >
                  {cmd}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Input row */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleChips}
            aria-expanded={chipsVisible}
            aria-label={chipsVisible ? 'Hide shortcuts' : 'Show shortcuts'}
            title={chipsVisible ? 'Hide shortcuts' : 'Show shortcuts'}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent"
          >
            {chipsVisible ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.xlsx,.pptx"
            data-testid="dock-file-input"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadDocument(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            title="Create a document, sheet or deck"
            data-testid="dock-open-create"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={docBusy}
            title="Upload a document (.docx, .xlsx, .pptx) — or type /new"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent disabled:opacity-50"
          >
            {docBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={toggleMic}
            disabled={loading || transcribing}
            title={recording ? 'Stop recording' : 'Dictate'}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border transition-colors disabled:opacity-50 ${
              recording ? 'animate-pulse bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
            }`}
          >
            {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
          </button>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              // nativeEvent.isComposing: don't submit mid-IME-composition.
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) submit();
            }}
            placeholder="Ask anything, or type / for commands…"
            className="h-9 flex-1 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button
            type="button"
            onClick={() => submit()}
            disabled={loading || !value.trim()}
            aria-label="Send"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <CreateArtifactDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        contextClientId={lastClientId ?? null}
      />
    </div>
  );
}
